'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  stravaProfileUrl: string;
  acceptTerms: boolean;
}

export default function EmailSignupForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { signUp } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>();

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        avatarUrl: data.fullName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2),
      });
      toast.success('Registrazione completata!', {
        description: 'Account creato. Un admin verificherà la tua iscrizione al club.',
      });
      setSubmitted(true);
    } catch (error: any) {
      toast.error(error?.message ?? 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 fade-in">
        <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircleIcon" size={32} className="text-green-400" />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-2">Registrazione completata!</h3>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto">
          Il tuo account è in attesa di verifica. Un admin del club IA Climb confirmerà la tua iscrizione entro 24–48 ore.
        </p>
        <div className="mt-4 p-3 bg-yellow-400/10 border border-yellow-400/20 rounded-lg">
          <p className="text-xs text-yellow-400 font-medium">
            ⚠️ Assicurati di essere iscritto al club Strava &quot;IA Climb&quot; per accelerare la verifica.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div>
        <label htmlFor="signup-name" className="block text-sm font-semibold text-foreground mb-1.5">Nome completo</label>
        <input id="signup-name" type="text" autoComplete="name" placeholder="Marco Bianchi" className="input-field w-full px-4 py-2.5 text-sm" {...register('fullName', { required: 'Nome obbligatorio', minLength: { value: 3, message: 'Minimo 3 caratteri' } })} />
        {errors.fullName && <p className="mt-1 text-xs text-red-400">{errors.fullName.message}</p>}
      </div>

      <div>
        <label htmlFor="signup-email" className="block text-sm font-semibold text-foreground mb-1.5">Email</label>
        <input id="signup-email" type="email" autoComplete="email" placeholder="marco@example.it" className="input-field w-full px-4 py-2.5 text-sm" {...register('email', { required: 'Email obbligatoria', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email non valida' } })} />
        {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
      </div>

      <div>
        <label htmlFor="signup-strava" className="block text-sm font-semibold text-foreground mb-1.5">
          Profilo Strava <span className="ml-1 text-xs text-muted-foreground font-normal">(URL o ID)</span>
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">Necessario per verificare la tua iscrizione al club IA Climb.</p>
        <input id="signup-strava" type="url" placeholder="https://www.strava.com/athletes/12345678" className="input-field w-full px-4 py-2.5 text-sm" {...register('stravaProfileUrl', { required: 'Profilo Strava obbligatorio', pattern: { value: /strava\.com\/athletes\//, message: 'URL Strava non valido (es: strava.com/athletes/12345678)' } })} />
        {errors.stravaProfileUrl && <p className="mt-1 text-xs text-red-400">{errors.stravaProfileUrl.message}</p>}
      </div>

      <div>
        <label htmlFor="signup-password" className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
        <div className="relative">
          <input id="signup-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Min. 8 caratteri" className="input-field w-full px-4 py-2.5 pr-10 text-sm" {...register('password', { required: 'Password obbligatoria', minLength: { value: 8, message: 'Minimo 8 caratteri' } })} />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
          </button>
        </div>
        {errors.password && <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>}
      </div>

      <div>
        <label htmlFor="signup-confirm" className="block text-sm font-semibold text-foreground mb-1.5">Conferma password</label>
        <div className="relative">
          <input id="signup-confirm" type={showConfirm ? 'text' : 'password'} autoComplete="new-password" placeholder="Ripeti la password" className="input-field w-full px-4 py-2.5 pr-10 text-sm" {...register('confirmPassword', { required: 'Conferma la password', validate: (val) => val === password || 'Le password non coincidono' })} />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
          </button>
        </div>
        {errors.confirmPassword && <p className="mt-1 text-xs text-red-400">{errors.confirmPassword.message}</p>}
      </div>

      <div>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0" {...register('acceptTerms', { required: 'Devi accettare i termini' })} />
          <span className="text-sm text-muted-foreground leading-relaxed">
            Accetto i{' '}
            <button type="button" className="text-primary hover:underline font-medium">Termini di Servizio</button>
            {' '}e la{' '}
            <button type="button" className="text-primary hover:underline font-medium">Privacy Policy</button>.
            Confermo di essere iscritto al club Strava &quot;IA Climb&quot;.
          </span>
        </label>
        {errors.acceptTerms && <p className="mt-1 text-xs text-red-400">{errors.acceptTerms.message}</p>}
      </div>

      <button type="submit" disabled={loading} className="btn-primary w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" style={{ minHeight: '48px' }}>
        {loading ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Invio richiesta...
          </>
        ) : 'Registrati'}
      </button>
    </form>
  );
}